import { useState, useRef } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { useMutation } from "@tanstack/react-query"
import {
  Upload,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Download,
  ArrowLeft,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  importApi,
  ApiError,
  type ImportRow,
  type ImportResult,
  type DealImportRow,
  type DealImportResult,
} from "@/lib/api"

type ImportMode = "contacts" | "deals"

type KnownField = {
  key: string
  label: string
  required?: boolean
}

const CONTACT_FIELDS: readonly KnownField[] = [
  { key: "firstName", label: "First Name", required: true },
  { key: "lastName", label: "Last Name", required: true },
  { key: "email", label: "Email" },
  { key: "mobile", label: "Mobile" },
  { key: "businessName", label: "Business Name" },
  { key: "abn", label: "ABN" },
  { key: "industry", label: "Industry" },
  { key: "source", label: "Source" },
  { key: "addressSuburb", label: "Suburb" },
  { key: "addressState", label: "State" },
  { key: "addressPostcode", label: "Postcode" },
  { key: "notes", label: "Notes" },
] as const

const DEAL_FIELDS: readonly KnownField[] = [
  // Applicant
  { key: "applicantFirstName", label: "Applicant First Name", required: true },
  { key: "applicantLastName", label: "Applicant Last Name", required: true },
  { key: "applicantTitle", label: "Applicant Title" },
  { key: "applicantEmail", label: "Applicant Email" },
  { key: "applicantMobile", label: "Applicant Mobile" },
  { key: "applicantDob", label: "Applicant DOB" },
  { key: "applicantAddress", label: "Applicant Address" },
  // Co-applicant
  { key: "applicant2Title", label: "Applicant 2 Title" },
  { key: "applicant2FirstName", label: "Applicant 2 First Name" },
  { key: "applicant2LastName", label: "Applicant 2 Last Name" },
  { key: "applicant2Email", label: "Applicant 2 Email" },
  { key: "applicant2Mobile", label: "Applicant 2 Mobile" },
  // Business
  { key: "businessName", label: "Business Name" },
  { key: "businessAbn", label: "Business ABN" },
  { key: "businessType", label: "Business Type" },
  { key: "isAssetBacked", label: "Is Asset Backed" },
  { key: "hasInvestmentProperty", label: "Has Investment Property" },
  // Deal
  { key: "brokerName", label: "Broker" },
  { key: "brokerEmail", label: "Broker Email" },
  { key: "lender", label: "Lender" },
  { key: "appType", label: "App Type" },
  { key: "loanType", label: "Loan Type" },
  { key: "term", label: "Term" },
  { key: "writingRate", label: "Writing Rate" },
  { key: "naf", label: "NAF" },
  { key: "balloon", label: "Balloon" },
  { key: "commission", label: "Commission" },
  { key: "remittanceDate", label: "Remittance Date" },
  { key: "termEndDate", label: "Term End Date" },
  { key: "settledDate", label: "Settled" },
] as const

function fieldsForMode(mode: ImportMode): readonly KnownField[] {
  return mode === "deals" ? DEAL_FIELDS : CONTACT_FIELDS
}

type FieldKey = string

const CONTACT_AUTO_MAP: Record<string, FieldKey> = {
  "first name": "firstName",
  firstname: "firstName",
  "given name": "firstName",
  "last name": "lastName",
  lastname: "lastName",
  surname: "lastName",
  "family name": "lastName",
  email: "email",
  "email address": "email",
  mobile: "mobile",
  phone: "mobile",
  "mobile number": "mobile",
  "business name": "businessName",
  business: "businessName",
  company: "businessName",
  abn: "abn",
  industry: "industry",
  source: "source",
  "source of enquiry": "source",
  suburb: "addressSuburb",
  city: "addressSuburb",
  state: "addressState",
  postcode: "addressPostcode",
  postal_code: "addressPostcode",
  zip: "addressPostcode",
  notes: "notes",
}

/** Auto-mapping tuned to the aggregator export Andy provided. */
const DEAL_AUTO_MAP: Record<string, FieldKey> = {
  broker: "brokerName",
  "broker name": "brokerName",
  "broker email": "brokerEmail",
  lender: "lender",
  "app type": "appType",
  "loan type": "loanType",
  term: "term",
  "writing rate": "writingRate",
  "business name": "businessName",
  "business abn": "businessAbn",
  "business type": "businessType",
  "applicant email": "applicantEmail",
  "applicant mobile": "applicantMobile",
  "applicant 2 email": "applicant2Email",
  "applicant 2 mobile": "applicant2Mobile",
  "applicant title": "applicantTitle",
  "applicant first name": "applicantFirstName",
  "applicant last name": "applicantLastName",
  "applicant dob": "applicantDob",
  "applicant address": "applicantAddress",
  "is asset backed": "isAssetBacked",
  "has investment property": "hasInvestmentProperty",
  "applicant 2 title": "applicant2Title",
  "applicant 2 first name": "applicant2FirstName",
  "applicant 2 last name": "applicant2LastName",
  "remittance date": "remittanceDate",
  "term end date": "termEndDate",
  settled: "settledDate",
  "settled date": "settledDate",
  naf: "naf",
  balloon: "balloon",
  commission: "commission",
}

function autoMapFor(mode: ImportMode): Record<string, FieldKey> {
  return mode === "deals" ? DEAL_AUTO_MAP : CONTACT_AUTO_MAP
}

function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  // Simple CSV parser handling quoted values with commas
  const lines: string[][] = []
  let cur: string[] = []
  let field = ""
  let inQuotes = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"'
        i++
      } else if (ch === '"') {
        inQuotes = false
      } else {
        field += ch
      }
    } else {
      if (ch === '"') inQuotes = true
      else if (ch === ",") {
        cur.push(field)
        field = ""
      } else if (ch === "\n" || ch === "\r") {
        if (ch === "\r" && text[i + 1] === "\n") i++
        cur.push(field)
        if (cur.some((v) => v.trim() !== "")) lines.push(cur)
        cur = []
        field = ""
      } else field += ch
    }
  }
  if (field.length > 0 || cur.length > 0) {
    cur.push(field)
    if (cur.some((v) => v.trim() !== "")) lines.push(cur)
  }
  if (lines.length === 0) return { headers: [], rows: [] }
  const [headers, ...rows] = lines
  return { headers: headers!.map((h) => h.trim()), rows }
}

function autoMap(
  headers: string[],
  lookup: Record<string, FieldKey>
): Record<number, FieldKey | ""> {
  const map: Record<number, FieldKey | ""> = {}
  headers.forEach((h, i) => {
    const norm = h.toLowerCase().trim().replace(/[_-]/g, " ").replace(/\s+/g, " ")
    map[i] = lookup[norm] ?? ""
  })
  return map
}

export default function ImportPage() {
  const [searchParams] = useSearchParams()
  const mode: ImportMode = searchParams.get("mode") === "deals" ? "deals" : "contacts"
  const KNOWN_FIELDS = fieldsForMode(mode)
  const AUTO_MAP = autoMapFor(mode)

  const fileRef = useRef<HTMLInputElement>(null)
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<string[][]>([])
  const [mapping, setMapping] = useState<Record<number, FieldKey | "">>({})
  const [skipDuplicates, setSkipDuplicates] = useState(true)
  // Optional batch source — applied to every row that doesn't have its own
  // source column. Broker types e.g. "CompareFirst batch Jul 2026" once and
  // every imported contact is tagged with that source for later filtering.
  const [batchSource, setBatchSource] = useState("")
  const [result, setResult] = useState<ImportResult | DealImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleFile = async (file: File) => {
    setError(null)
    setResult(null)
    const text = await file.text()
    const parsed = parseCsv(text)
    if (parsed.headers.length === 0) {
      setError("File appears to be empty")
      return
    }
    setHeaders(parsed.headers)
    setRows(parsed.rows)
    setMapping(autoMap(parsed.headers, AUTO_MAP))
  }

  const runImport = useMutation({
    mutationFn: async () => {
      // Build mapped row objects
      const mapped: Record<string, string>[] = rows.map((r) => {
        const obj: Record<string, string> = {}
        Object.entries(mapping).forEach(([idx, field]) => {
          if (!field) return
          const value = r[Number(idx)]?.trim()
          if (value) obj[field] = value
        })
        return obj
      })

      if (mode === "deals") {
        // Deals import requires applicantFirstName + applicantLastName
        const valid = mapped.filter((r) => r.applicantFirstName && r.applicantLastName) as unknown as DealImportRow[]
        if (valid.length === 0)
          throw new Error("No rows have both Applicant First Name and Applicant Last Name mapped")
        return importApi.deals(valid, batchSource.trim() || undefined)
      }

      // Contacts import
      const valid = mapped.filter((r) => r.firstName && r.lastName) as unknown as ImportRow[]
      if (valid.length === 0)
        throw new Error("No rows have both First Name and Last Name mapped")
      return importApi.contacts(valid, skipDuplicates, batchSource.trim() || undefined)
    },
    onSuccess: setResult,
    onError: (err) => setError(err instanceof ApiError ? err.message : (err as Error).message),
  })

  const usedFields = new Set(Object.values(mapping).filter(Boolean))
  const missingRequired = KNOWN_FIELDS.filter((f) => f.required && !usedFields.has(f.key))

  const downloadSample = () => {
    const csv =
      mode === "deals"
        ? "Broker,Lender,App Type,Loan Type,Term,Writing Rate,Business Name,Business ABN,Applicant Email,Applicant Mobile,Applicant 2 Email,Applicant 2 Mobile,Applicant Title,Applicant First Name,Applicant Last Name,Applicant DOB,Applicant Address,Is Asset Backed,Has Investment Property,Business Type,Applicant 2 Title,Applicant 2 First Name,Applicant 2 Last Name,Remittance Date,Term End Date,Settled,NAF,Balloon,Commission\n" +
          'Andy Broker,Pepper Money,Consumer,Vehicle Finance,60,8.99,,,sarah@example.com,0400111222,,,,Sarah,Nguyen,1988-05-14,"12 Smith St, Brisbane QLD 4000",TRUE,FALSE,,,,,2024-05-15,2029-05-15,2024-05-10,45000,5000,1200\n'
        : "First Name,Last Name,Email,Mobile,Business Name,ABN,Industry,Source,Suburb,State,Postcode\n" +
          "Sarah,Nguyen,sarah@example.com,0400111222,Nguyen Transport Pty Ltd,51824753556,Transport,Referral,Brisbane,QLD,4000\n" +
          "James,Wilson,james@example.com,0411333444,,,Retail,Web,Melbourne,VIC,3000\n"
    const blob = new Blob([csv], { type: "text/csv" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = `rivercity-${mode}-template.csv`
    link.click()
  }

  return (
    <AppShell
      title={mode === "deals" ? "Import Deals + Contacts" : "Import Contacts"}
      subtitle={
        mode === "deals"
          ? "Bulk upload past, present, and future deals with linked applicant contacts (matches on ABN / email / mobile)."
          : "Bulk upload contacts from a CSV file"
      }
    >
      <div className="mx-auto max-w-4xl space-y-6">
        {result ? (
          <ResultCard result={result} onReset={() => {
            setResult(null)
            setHeaders([])
            setRows([])
            setMapping({})
            if (fileRef.current) fileRef.current.value = ""
          }} />
        ) : (
          <>
            {/* Upload */}
            <Card className="p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold text-foreground">1. Upload CSV file</h2>
                  <p className="text-muted-foreground mt-1 text-sm">
                    First row must be column headers. Common column names are auto-detected.
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={downloadSample}>
                  <Download className="h-4 w-4" />
                  Download template
                </Button>
              </div>
              <div className="mt-4">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) handleFile(f)
                  }}
                  className="border-input file:bg-accent file:hover:bg-accent/80 flex h-11 w-full rounded-md border bg-background px-3 py-2 text-sm file:mr-3 file:h-full file:border-0 file:px-3 file:text-sm file:font-medium file:cursor-pointer"
                />
              </div>
            </Card>

            {/* Mapping */}
            {headers.length > 0 && (
              <Card className="p-6">
                <h2 className="text-base font-semibold text-foreground">
                  2. Map columns ({rows.length} row{rows.length === 1 ? "" : "s"} detected)
                </h2>
                <p className="text-muted-foreground mt-1 text-sm">
                  Match each CSV column to a CRM field. Skip unwanted columns.
                </p>
                <div className="mt-4 overflow-hidden rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-rc-grey-50 text-muted-foreground text-xs uppercase tracking-wider">
                      <tr>
                        <th className="p-2 text-left font-medium">CSV column</th>
                        <th className="p-2 text-left font-medium">CRM field</th>
                        <th className="p-2 text-left font-medium">Sample</th>
                      </tr>
                    </thead>
                    <tbody>
                      {headers.map((h, idx) => (
                        <tr key={idx} className="border-t border-border">
                          <td className="p-2 font-mono text-xs">{h}</td>
                          <td className="p-2">
                            <select
                              className="border-input h-9 w-full rounded-md border bg-background px-2 text-sm"
                              value={mapping[idx] ?? ""}
                              onChange={(e) =>
                                setMapping((m) => ({ ...m, [idx]: e.target.value as FieldKey | "" }))
                              }
                            >
                              <option value="">— Skip —</option>
                              {KNOWN_FIELDS.map((f) => (
                                <option key={f.key} value={f.key}>
                                  {f.label}
                                  {f.required ? " *" : ""}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="text-muted-foreground p-2 text-xs">
                            {rows[0]?.[idx]?.slice(0, 40) ?? ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {missingRequired.length > 0 && (
                  <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    Missing required mapping:{" "}
                    {missingRequired.map((f) => (
                      <Badge key={f.key} variant="warning" className="ml-1 text-[10px]">
                        {f.label}
                      </Badge>
                    ))}
                  </div>
                )}
                {mode === "contacts" && (
                  <div className="mt-4 flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                      type="checkbox"
                      checked={skipDuplicates}
                      onChange={(e) => setSkipDuplicates(e.target.checked)}
                    />
                    Skip existing contacts (matched by email / mobile / ABN)
                    </label>
                  </div>
                )}
                {mode === "deals" && (
                  <p className="mt-4 text-xs text-muted-foreground">
                    Existing contacts are matched by email, mobile, or ABN. A new contact is created only when no match is found.
                  </p>
                )}
                <div className="mt-4 flex flex-col gap-1 border-t border-border/50 pt-4">
                  <label className="text-sm font-medium text-foreground" htmlFor="batch-source">
                    Batch source (optional)
                  </label>
                  <input
                    id="batch-source"
                    type="text"
                    placeholder="e.g. CompareFirst batch Jul 2026"
                    value={batchSource}
                    onChange={(e) => setBatchSource(e.target.value)}
                    className="border-input bg-background h-10 max-w-md rounded-md border px-3 text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Applied to every row without an explicit source column. Lets you slice imported contacts by source later.
                  </p>
                </div>
              </Card>
            )}

            {/* Preview */}
            {headers.length > 0 && (
              <Card className="p-6">
                <h2 className="text-base font-semibold text-foreground">
                  3. Preview (first 5 rows)
                </h2>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-rc-grey-50 text-muted-foreground text-xs uppercase tracking-wider">
                      <tr>
                        {KNOWN_FIELDS.filter((f) => usedFields.has(f.key)).map((f) => (
                          <th key={f.key} className="p-2 text-left font-medium">
                            {f.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 5).map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          {KNOWN_FIELDS.filter((f) => usedFields.has(f.key)).map((f) => {
                            const colIdx = Object.entries(mapping).find(([, v]) => v === f.key)?.[0]
                            const val = colIdx !== undefined ? r[Number(colIdx)] : ""
                            return (
                              <td key={f.key} className="p-2 text-xs">
                                {val ?? ""}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {error && (
              <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
                {error}
              </div>
            )}

            {headers.length > 0 && (
              <div className="flex justify-end gap-3">
                <Button variant="outline" asChild>
                  <Link to="/contacts">Cancel</Link>
                </Button>
                <Button
                  onClick={() => runImport.mutate()}
                  disabled={runImport.isPending || missingRequired.length > 0}
                >
                  {runImport.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  Import {rows.length} row{rows.length === 1 ? "" : "s"}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  )
}

function ResultCard({
  result,
  onReset,
}: {
  result: ImportResult | DealImportResult
  onReset: () => void
}) {
  const isDeal = "dealsCreated" in result
  return (
    <Card className="p-8 text-center">
      <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-emerald-100 text-emerald-700">
        <CheckCircle2 className="h-6 w-6" />
      </div>
      <h2 className="text-lg font-semibold text-foreground">Import complete</h2>
      {isDeal ? (
        <div className="mt-4 grid grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-2xl font-semibold text-emerald-600">{result.dealsCreated}</div>
            <div className="text-muted-foreground text-xs">Deals created</div>
          </div>
          <div>
            <div className="text-2xl font-semibold text-foreground">{result.contactsCreated}</div>
            <div className="text-muted-foreground text-xs">New contacts</div>
          </div>
          <div>
            <div className="text-2xl font-semibold text-amber-600">{result.contactsMatched}</div>
            <div className="text-muted-foreground text-xs">Contacts matched</div>
          </div>
          <div>
            <div className="text-destructive text-2xl font-semibold">{result.errors.length}</div>
            <div className="text-muted-foreground text-xs">Errors</div>
          </div>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-2xl font-semibold text-foreground">{result.created}</div>
            <div className="text-muted-foreground text-xs">Created</div>
          </div>
          <div>
            <div className="text-2xl font-semibold text-amber-600">{result.skipped}</div>
            <div className="text-muted-foreground text-xs">Skipped (duplicates)</div>
          </div>
          <div>
            <div className="text-destructive text-2xl font-semibold">{result.errors.length}</div>
            <div className="text-muted-foreground text-xs">Errors</div>
          </div>
        </div>
      )}
      {result.errors.length > 0 && (
        <div className="border-destructive/40 bg-destructive/5 mt-6 rounded-md border p-3 text-left text-xs">
          <div className="mb-1 font-medium text-destructive flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" />
            Errors
          </div>
          <ul className="space-y-1">
            {result.errors.slice(0, 20).map((e, i) => (
              <li key={i}>
                Row {e.row}: {e.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-6 flex justify-center gap-3">
        <Button variant="outline" onClick={onReset}>
          <ArrowLeft className="h-4 w-4" />
          Import another file
        </Button>
        <Button asChild>
          <Link to="/contacts">View contacts</Link>
        </Button>
      </div>
    </Card>
  )
}
